

import { zodResolver } from '@hookform/resolvers/zod';
import { SquarePen, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useUpdatePackage } from '../hooks/useUpdatePackage';
import { useUploadPackageVideo } from '../hooks/useUploadPackageVideo';

const packageSchema = z.object({
  name: z.string().min(1, 'Package name is required').max(200, 'Package name cannot exceed 200 characters'),
  description: z
    .string()
    .min(250, 'Enter a description between 250 and 500 characters.')
    .max(500, 'Enter a description between 250 and 500 characters.')
    .optional()
    .or(z.literal('')),
  bookingPrice: z.number().min(0, 'Booking price cannot be negative'),
  duration: z.number().min(0, 'Duration cannot be negative').optional(),
  durationType: z.enum(['minutes', 'hours', 'days', 'months']).optional(),
});

export const EditPackageModal = (props) => {
  const { isEditModalOpen, setIsEditModalOpen, package: pkg } = props;
  const { mutate: updatePackage, isPending } = useUpdatePackage();

  const uploadVideoMutation = useUploadPackageVideo();
  const uploadImageMutation = useUploadPackageVideo();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: 'onChange',
    resolver: zodResolver(packageSchema),
  });

  const [videoFiles, setVideoFiles] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);

  // Each image: { key: string, preSignedUrl: string, isPrimary: bool, caption: null }
  const [images, setImages] = useState([]);

  // Each video: { key: string, preSignedUrl: string }
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    if (pkg) {
      reset({
        name: pkg.name || '',
        description: pkg.description || '',
        bookingPrice: pkg.pricing?.bookingPrice || 0,
        duration: pkg.duration || 0,
        durationType: pkg.durationType || 'hours',
      });

      // Normalize existing videos — support plain strings and objects, skip nulls
      setVideos(
        (pkg.videos || [])
          .filter((v) => v != null)
          .map((v) => {
            if (typeof v === 'string') return { key: v, preSignedUrl: v };
            const key = v.key || v.url;
            return {
              key,
              preSignedUrl: v.preSignedUrl || v.presignedUrl || key,
            };
          })
      );

      // Normalize existing images — package schema stores plain strings (S3 keys)
      setImages(
        (pkg.images || [])
          .filter((img) => img != null)
          .map((img) => {
            // Plain string from DB
            if (typeof img === 'string') return { key: img, preSignedUrl: img };
            // Object shape (freshly uploaded in same session)
            const key = img.key || img.url;
            return {
              key,
              preSignedUrl: img.preSignedUrl || img.presignedUrl || key,
              isPrimary: img.isPrimary || false,
              caption: img.caption || null,
            };
          })
      );
    }
  }, [pkg, reset]);

  const onSubmit = (data) => {
    const payload = {
      packageId: pkg._id,
      data: {
        name: data.name,
        description: data.description || undefined,
        bookingPrice: data.bookingPrice,
        duration: data.duration,
        durationType: data.durationType,
        // Send only keys to DB
        videos: videos.map((v) => v.key),
        images: images.map((img) => img.key),
      },
    };

    updatePackage(payload, {
      onSuccess: () => {
        reset();
        setIsEditModalOpen(false);
      },
    });
  };

  if (!isEditModalOpen || !pkg) return null;

  return (
    <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'>
      <div className='bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto custom-scrollbar'>

        {/* Header */}
        <div className='sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between'>
          <h2 className='text-lg font-semibold'>Edit Package</h2>
          <button
            onClick={() => setIsEditModalOpen(false)}
            className='inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
            aria-label='Close edit package modal'
          >
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className='p-6 space-y-6'>

          {/* Package Name */}
          <div>
            <label className='block text-sm font-medium mb-1'>Package Name *</label>
            <input
              type='text'
              {...register('name')}
              className='w-full px-3 py-2 text-sm'
              placeholder='Enter package name'
            />
            {errors.name && <p className='text-destructive text-xs mt-1'>{errors.name.message}</p>}
          </div>

          {/* Description */}
          <div>
            <label className='block text-sm font-medium mb-1'>Description</label>
            <textarea
              {...register('description')}
              rows={4}
              className='w-full px-3 py-2 text-sm'
              placeholder='Enter package description (250 to 500 characters)'
            />
            {errors.description && <p className='text-destructive text-xs mt-1'>{errors.description.message}</p>}
          </div>

          {/* Pricing */}
          <div className='space-y-4'>
            <h3 className='text-sm font-semibold text-foreground'>Pricing</h3>
            <div>
              <label className='block text-sm font-medium mb-1'>Booking Price (INR) *</label>
              <input
                type='number'
                step='0.01'
                {...register('bookingPrice', { valueAsNumber: true })}
                className='w-full px-3 py-2 text-sm'
                placeholder='0.00'
              />
              {errors.bookingPrice && <p className='text-destructive text-xs mt-1'>{errors.bookingPrice.message}</p>}
            </div>
          </div>

          {/* Duration */}
          <div className='space-y-4'>
            <h3 className='text-sm font-semibold text-foreground'>Duration</h3>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium mb-1'>Duration</label>
                <input
                  type='number'
                  {...register('duration', { valueAsNumber: true })}
                  className='w-full px-3 py-2 text-sm'
                  placeholder='0'
                />
                {errors.duration && <p className='text-destructive text-xs mt-1'>{errors.duration.message}</p>}
              </div>
              <div>
                <label className='block text-sm font-medium mb-1'>Duration Type</label>
                <select {...register('durationType')} className='w-full px-3 py-2 text-sm'>
                  <option value='minutes'>Minutes</option>
                  <option value='hours'>Hours</option>
                  <option value='days'>Days</option>
                  <option value='months'>Months</option>
                </select>
              </div>
            </div>
          </div>

          {/* Seller Info (Read-only) */}
          {pkg.sellerId && (
            <div className='bg-muted rounded-lg p-4'>
              <h3 className='text-sm font-semibold text-foreground mb-2'>Seller Information</h3>
              <p className='text-sm text-muted-foreground'>
                {pkg.sellerId?.firstName || pkg.sellerId?.lastName
                  ? `${pkg.sellerId?.firstName || ''} ${pkg.sellerId?.lastName || ''}`.trim()
                  : pkg.sellerId?.phoneNumber || 'N/A'}
              </p>
            </div>
          )}

          {/* IMAGES */}
          <div className='space-y-4'>
            <h3 className='text-sm font-semibold'>Images</h3>

            <input
              type='file'
              accept='image/*'
              multiple
              onChange={(e) => setImageFiles(Array.from(e.target.files))}
            />

            {images.length > 0 && (
              <div className='grid grid-cols-2 md:grid-cols-3 gap-4'>
                {images.map((image, i) => (
                  <div key={i} className='border rounded-lg p-2'>
                    <img
                      // Use preSignedUrl for preview
                      src={image.preSignedUrl}
                      alt='package'
                      className='w-full h-32 object-cover rounded'
                    />
                    <button
                      type='button'
                      onClick={() =>
                        setImages((prev) => prev.filter((_, index) => index !== i))
                      }
                      className='w-full mt-2 px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600'
                    >
                      Remove Image
                    </button>
                  </div>
                ))}
              </div>
            )}

            <button
              type='button'
              disabled={uploadImageMutation.isPending || !imageFiles.length}
              onClick={async () => {
                const uploaded = [...images];

                for (const file of imageFiles) {
                  const res = await uploadImageMutation.mutateAsync(file);
                  // Store key (for DB) + preSignedUrl (for preview)
                  uploaded.push({
                    key: res?.data?.key,
                    preSignedUrl: res?.data?.preSignedUrl,
                    isPrimary: false,
                    caption: null,
                  });
                }

                setImages(uploaded);
                setImageFiles([]);
              }}
              className='px-4 py-2 rounded-lg bg-primary text-white disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {uploadImageMutation.isPending ? 'Uploading...' : 'Upload Images'}
            </button>
          </div>

          {/* VIDEOS */}
          <div className='space-y-4'>
            <h3 className='text-sm font-semibold'>Videos</h3>

            <input
              type='file'
              accept='video/*'
              multiple
              onChange={(e) => setVideoFiles(Array.from(e.target.files))}
            />

            {videos.length > 0 && (
              <div className='grid grid-cols-2 md:grid-cols-3 gap-4'>
                {videos.map((video, i) => (
                  <div key={i} className='border rounded-lg p-2'>
                    <video
                      // Use preSignedUrl for preview
                      src={video.preSignedUrl}
                      controls
                      className='w-full h-32 object-cover rounded'
                    />
                    <button
                      type='button'
                      onClick={() =>
                        setVideos((prev) => prev.filter((_, index) => index !== i))
                      }
                      className='px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600'
                    >
                      Remove Video
                    </button>
                  </div>
                ))}
              </div>
            )}

            <button
              type='button'
              disabled={uploadVideoMutation.isPending || !videoFiles.length}
              onClick={async () => {
                const uploaded = [...videos];

                for (const file of videoFiles) {
                  const res = await uploadVideoMutation.mutateAsync(file);
                  // Store key (for DB) + preSignedUrl (for preview)
                  uploaded.push({
                    key: res?.data?.key,
                    preSignedUrl: res?.data?.preSignedUrl,
                  });
                }

                setVideos(uploaded);
                setVideoFiles([]);
              }}
              className='px-4 py-2 rounded-lg bg-primary text-white disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {uploadVideoMutation.isPending ? 'Uploading...' : 'Upload Videos'}
            </button>
          </div>

          {/* Actions */}
          <div className='flex justify-end space-x-3 pt-4 border-t border-border'>
            <button
              type='button'
              onClick={() => setIsEditModalOpen(false)}
              className='px-4 py-2 text-sm border border-border rounded-md hover:bg-accent transition-colors'
            >
              Cancel
            </button>
            <button
              type='submit'
              disabled={isSubmitting || isPending}
              className='px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50'
            >
              {isPending ? (
                <div className='flex items-center justify-center'>
                  <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2' />
                  Updating...
                </div>
              ) : (
                <div className='flex items-center justify-center'>
                  <SquarePen size={16} className='mr-2' />
                  Update Package
                </div>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPackageModal;
